import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import MonthlyAllocationService from "../../services/MonthlyAllocationService";

// Get all monthly allocations
export const getAllMonthlyAllocations = createAsyncThunk(
    "monthlyAllocation/getAllMonthlyAllocations",
    async (_, { rejectWithValue }) => {
        try {
            const response = await MonthlyAllocationService.GetAllMonthlyAllocations();

            return (
                response.data?.ResultSet ||
                response.data?.resultSet ||
                response.data ||
                []
            );
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Message ||
                error.message ||
                "Failed to get monthly allocations"
            );
        }
    }
);

// Get monthly allocation by ID
export const getMonthlyAllocationById = createAsyncThunk(
    "monthlyAllocation/getMonthlyAllocationById",
    async (id, { rejectWithValue }) => {
        try {
            const response = await MonthlyAllocationService.GetMonthlyAllocationById(id);

            return (
                response.data?.ResultSet ||
                response.data?.resultSet ||
                response.data ||
                []
            );
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Message ||
                error.message ||
                "Failed to get monthly allocation"
            );
        }
    }
);

// Get monthly allocations by year and month
export const getMonthlyAllocationByYearAndMonth = createAsyncThunk(
    "monthlyAllocation/getMonthlyAllocationByYearAndMonth",
    async ({ year, month }, { rejectWithValue }) => {
        try {
            const response = await MonthlyAllocationService.GetMonthlyAllocationByYearAndMonth(year, month);

            return (
                response.data?.ResultSet ||
                response.data?.resultSet ||
                response.data ||
                []
            );
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Message ||
                error.message ||
                "Failed to get monthly allocations"
            );
        }
    }
);

// Add monthly allocation
export const addMonthlyAllocation = createAsyncThunk(
    "monthlyAllocation/addMonthlyAllocation",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await MonthlyAllocationService.AddMonthlyAllocation(payload);

            return (
                response.data?.ResultSet ||
                response.data?.resultSet ||
                response.data ||
                []
            );
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Message ||
                error.message ||
                "Failed to add monthly allocation"
            );
        }
    }
);

// Update monthly allocation
export const updateMonthlyAllocation = createAsyncThunk(
    "monthlyAllocation/updateMonthlyAllocation",
    async (payload, { rejectWithValue }) => {
        try {
            const response =
                await MonthlyAllocationService.UpdateMonthlyAllocation(payload);

            return (
                response.data?.ResultSet ||
                response.data?.resultSet ||
                response.data ||
                []
            );
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Message ||
                error.message ||
                "Failed to update monthly allocation"
            );
        }
    }
);

// Update monthly allocation status
export const updateMonthlyAllocationStatus = createAsyncThunk(
    "monthlyAllocation/updateMonthlyAllocationStatus",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await MonthlyAllocationService.UpdateMonthlyAllocationStatus(payload);

            return (
                response.data?.ResultSet ||
                response.data?.resultSet ||
                response.data ||
                []
            );
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Message ||
                error.message ||
                "Failed to update monthly allocation status"
            );
        }
    }
);

const initialState = {
    allocations: [],
    selectedAllocation: null,

    loading: false,
    saving: false,

    error: null,
    success: false,
};

const monthlyAllocationSlice = createSlice({
    name: "monthlyAllocation",
    initialState,

    reducers: {
        clearMonthlyAllocationError: (state) => {
            state.error = null;
        },

        clearMonthlyAllocationSuccess: (state) => {
            state.success = false;
        },

        clearMonthlyAllocations: (state) => {
            state.allocations = [];
        },

        clearSelectedAllocation: (state) => {
            state.selectedAllocation = null;
        },
    },

    extraReducers: (builder) => {
        builder

            // Get All Monthly Allocations
            .addCase(getAllMonthlyAllocations.pending, (state) => {
                state.loading = true;
                state.error = null;
            })

            .addCase(
                getAllMonthlyAllocations.fulfilled,
                (state, action) => {
                    state.loading = false;
                    state.allocations = action.payload;
                }
            )

            .addCase(
                getAllMonthlyAllocations.rejected,
                (state, action) => {
                    state.loading = false;
                    state.error =
                        action.payload ||
                        "Failed to load monthly allocations";
                }
            )

            // Get Monthly Allocation By ID
            .addCase(getMonthlyAllocationById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })

            .addCase(
                getMonthlyAllocationById.fulfilled,
                (state, action) => {
                    state.loading = false;
                    state.selectedAllocation = action.payload;
                }
            )

            .addCase(
                getMonthlyAllocationById.rejected,
                (state, action) => {
                    state.loading = false;
                    state.error =
                        action.payload ||
                        "Failed to load monthly allocation";
                }
            )

            // Get Monthly Allocation By Year And Month
            .addCase(
                getMonthlyAllocationByYearAndMonth.pending,
                (state) => {
                    state.loading = true;
                    state.error = null;
                }
            )

            .addCase(
                getMonthlyAllocationByYearAndMonth.fulfilled,
                (state, action) => {
                    state.loading = false;
                    state.allocations = action.payload;
                }
            )

            .addCase(
                getMonthlyAllocationByYearAndMonth.rejected,
                (state, action) => {
                    state.loading = false;
                    state.error =
                        action.payload ||
                        "Failed to load monthly allocations";
                }
            )

            // Add Monthly Allocation
            .addCase(addMonthlyAllocation.pending, (state) => {
                state.saving = true;
                state.error = null;
                state.success = false;
            })

            .addCase(addMonthlyAllocation.fulfilled, (state) => {
                state.saving = false;
                state.success = true;
            })

            .addCase(addMonthlyAllocation.rejected, (state, action) => {
                state.saving = false;
                state.error =
                    action.payload ||
                    "Failed to add monthly allocation";
            })

            // Update Monthly Allocation
            .addCase(updateMonthlyAllocation.pending, (state) => {
                state.saving = true;
                state.error = null;
                state.success = false;
            })

            .addCase(updateMonthlyAllocation.fulfilled, (state, action) => {
                state.saving = false;
                state.success = true;
            })

            .addCase(updateMonthlyAllocation.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload || "Failed to update monthly allocation";
            })

            // Update Monthly Allocation Status
            .addCase(
                updateMonthlyAllocationStatus.pending,
                (state) => {
                    state.saving = true;
                    state.error = null;
                    state.success = false;
                }
            )

            .addCase(
                updateMonthlyAllocationStatus.fulfilled,
                (state) => {
                    state.saving = false;
                    state.success = true;
                }
            )

            .addCase(
                updateMonthlyAllocationStatus.rejected,
                (state, action) => {
                    state.saving = false;
                    state.error =
                        action.payload ||
                        "Failed to update monthly allocation status";
                }
            );
    },
});

export const {
    clearMonthlyAllocationError,
    clearMonthlyAllocationSuccess,
    clearMonthlyAllocations,
    clearSelectedAllocation,
} = monthlyAllocationSlice.actions;

export default monthlyAllocationSlice.reducer;
